import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import React from 'react';
import CanvasEmbeddedPage from '../../components/canvas/CanvasEmbeddedPage';
import ErrorBoundary from '../../components/ErrorBoundary';
import {
  NoDuplicatesState,
  ResolverLoadingState,
} from './components/DuplicateEmptyState';
import { DeleteProgressBar } from './components/DeleteProgressBar';
import PlanDone from './components/PlanDone';
import PlanOverview from './components/PlanOverview';
import ReviewStage from './components/ReviewStage';
import './DuplicateResolverPage.scss';
import { useDuplicateCrateIndex } from './useDuplicateCrateIndex';
import { useDuplicateGroupMutations } from './useDuplicateGroupMutations';
import { useDuplicatePlan } from './useDuplicatePlan';
import { useDuplicateResolver } from './useDuplicateResolver';

type ResolverProps = {
  /**
   * Mounted inside a homepage canvas rather than routed to. There is no Ionic
   * view lifecycle to load on and no history to go back through.
   */
  embedded?: boolean;
};

const DuplicateResolverPage = React.memo(({ embedded }: ResolverProps) => {
  const core = useDuplicateResolver(embedded);
  const mutations = useDuplicateGroupMutations(core);
  const crateIndex = useDuplicateCrateIndex();
  const plan = useDuplicatePlan(core, mutations, crateIndex);

  const busy = core.deleteProgress.inProgress;
  const hasGroups = core.duplicateGroupItems.length > 0;

  const body = (
    <>
      <IonHeader className="dup-header">
        <IonToolbar>
          {!embedded && (
            <IonButtons slot="start">
              <IonBackButton defaultHref="/" />
            </IonButtons>
          )}
          <IonTitle>Duplicates</IonTitle>
          {plan.stage !== 'overview' && (
            <IonButtons slot="end">
              <button
                type="button"
                className="dup-btn dup-btn--quiet"
                onClick={() => plan.setStage('overview')}
                disabled={busy}
              >
                Overview
              </button>
            </IonButtons>
          )}
        </IonToolbar>
      </IonHeader>

      <IonContent className="dup-content">
        {core.isLoading && !hasGroups ? (
          <ResolverLoadingState />
        ) : !hasGroups ? (
          <NoDuplicatesState />
        ) : plan.stage === 'overview' ? (
          <PlanOverview plan={plan} busy={busy} />
        ) : plan.stage === 'review' ? (
          <ReviewStage plan={plan} busy={busy} />
        ) : (
          <PlanDone plan={plan} />
        )}
      </IonContent>

      {busy && (
        <IonFooter className="dup-footer">
          <DeleteProgressBar progress={core.deleteProgress} />
        </IonFooter>
      )}
    </>
  );

  if (embedded) {
    return (
      <CanvasEmbeddedPage className="duplicate-resolver-page">
        {body}
      </CanvasEmbeddedPage>
    );
  }

  return <IonPage className="duplicate-resolver-page">{body}</IonPage>;
});

DuplicateResolverPage.displayName = 'DuplicateResolverPage';

const DuplicateResolverPageWithErrorBoundary: React.FC<ResolverProps> = ({
  embedded,
}) => (
  <ErrorBoundary>
    <DuplicateResolverPage embedded={embedded} />
  </ErrorBoundary>
);

export default DuplicateResolverPageWithErrorBoundary;
